import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { ContentPlaceholder } from '../../../../../../components/content-placeholder/content-placeholder';
import { STANDARD_DATE_FORMATS } from '../../../../../../helpers/date-time-helpers';
import { formatDate } from '../../../../../../helpers/date-time-helpers/formatting/formatting';
import { useMilestonesTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { ThroughputType } from '../../../../../../store/strategy-store/strategy-store.type';
import { graphCambridgeBlue, redBase, secondaryBase, skyDark, skyLight } from '../../../../../../styles/design-tokens';
import { getMetricLabel } from '../../../../strategy.helpers';
import { LineChartDot } from '../../../components/line-chart/line-chart-dot';
import { hasArrayData } from '../../../initiative-performance.helpers';
import { MilestoneProgressChartLegend } from './milestone-progress-chart-legend';
import { MilestoneProgressChartTooltip } from './milestone-progress-chart-tooltip';
import { getChartLineName } from './milestone-progress-chart.helpers';
import { useChartData } from './milestone-progress-chart.hooks';
import { DataKey } from './milestone-progress-chart.type';

export const MilestoneProgressChart = forwardRef<HTMLDivElement>((_, ref) => {
  const i18n = useTranslation();
  const selectedMilestone = useMilestonesTabStore((state) => state.selectedMilestone);
  const metric = useMilestonesTabStore((state) => state.metric);
  const visibleThroughputTypes = useMilestonesTabStore((state) => state.visibleThroughputTypes);

  const chartData = useChartData(metric);
  const hasData = hasArrayData(chartData);

  const currentMonthLabel = useMemo(() => formatDate(new Date(), { format: STANDARD_DATE_FORMATS.MONTH_YEAR }), []);

  if (!selectedMilestone || !hasData) {
    return <ContentPlaceholder />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <LineChart data={chartData} margin={{ top: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: getMetricLabel(metric, i18n.t),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
          />
          <RechartsTooltip content={<MilestoneProgressChartTooltip />} />
          <ReferenceLine x={currentMonthLabel} stroke={skyDark} yAxisId="left" />

          {/* Target End Date Line */}
          {chartData.length > 0 && chartData[0].targetEndDate && (
            <ReferenceLine
              x={chartData.find((item) => item.rawDate === chartData[0].targetEndDate)?.date || ''}
              stroke={redBase}
              yAxisId="left"
              strokeWidth={2}
              strokeDasharray="5 5"
            />
          )}

          {/* Historical Data - Solid Lines */}
          {visibleThroughputTypes[ThroughputType.Total] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="totalRegular"
              name={getChartLineName(DataKey.TotalRegular, metric, i18n.t)}
              stroke={secondaryBase}
              strokeWidth={1}
              dot={<LineChartDot color={secondaryBase} />}
              activeDot={<LineChartDot color={secondaryBase} />}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}
          {visibleThroughputTypes[ThroughputType.Completed] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="completedRegular"
              name={getChartLineName(DataKey.CompletedRegular, metric, i18n.t)}
              stroke={graphCambridgeBlue}
              strokeWidth={1}
              dot={<LineChartDot color={graphCambridgeBlue} />}
              activeDot={<LineChartDot color={graphCambridgeBlue} />}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}

          {/* Future Data - Dashed Lines */}
          {visibleThroughputTypes[ThroughputType.Total] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="totalFuture"
              name={getChartLineName(DataKey.TotalFuture, metric, i18n.t)}
              stroke={secondaryBase}
              strokeWidth={1}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}
          {visibleThroughputTypes[ThroughputType.Completed] && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="completedFuture"
              name={getChartLineName(DataKey.CompletedFuture, metric, i18n.t)}
              stroke={graphCambridgeBlue}
              strokeWidth={1}
              strokeDasharray="5 5"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={true}
            />
          )}

          <Legend content={<MilestoneProgressChartLegend />} verticalAlign="bottom" height={36} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});
